import { Auth } from '@util/Auth';
import { recordError } from '@util/Errors';
import { SaveInsuranceCoverageCheckData } from 'hydra-data-types';
import { omit } from 'ts-utils';
import { ApiClient } from '../../../api/ApiClient';
import { buildInsuranceCoverageCheck } from '../../../util/InsuranceCoverageCheck';
import { store as baseStore, StoreStateInternal } from '../../Store';
import { dispatchUserData } from '../UserDataActions';

let lastSyncedContent: string | undefined;
let inFlight: Promise<void> | undefined;
let rerunAfterInFlight = false;

/** What was said, not where it's filed: the id is the target document, not part of the answers. */
function contentOf(payload: SaveInsuranceCoverageCheckData): string {
  return JSON.stringify(omit(payload, ['id']));
}

/**
 * The flow config name identifies Direct vs Referred users for the coverage team. Loaded lazily:
 * a static import would drag every flow config (and their assets) into the store's module graph.
 */
async function getFlowName(state: StoreStateInternal): Promise<string> {
  const { chooseAssessmentConfig } = await import('../../../config/ChooseAssessmentConfig');
  return chooseAssessmentConfig(state).name;
}

/** Test hook: forget what has been synced so a fresh store starts clean. */
export function resetInsuranceCoverageCheckSync(): void {
  lastSyncedContent = undefined;
  inFlight = undefined;
  rerunAfterInFlight = false;
}

/**
 * Mirrors the completed coverage flow into `/insurance_coverage_check`, the collection the website
 * form also writes, so the coverage team sees one list. Runs after every answer merge and on login;
 * it only saves once the user is signed in (the callable stamps their userId and rejects anonymous
 * calls) and the flow is complete. The first save's id is kept in user data so later edits update
 * the same document, and unchanged answers are never re-sent. Failures are recorded, never
 * surfaced — the next merge retries. Overlapping runs coalesce: one save, then one re-check.
 */
export async function syncInsuranceCoverageCheck(store = baseStore): Promise<void> {
  if (inFlight !== undefined) {
    rerunAfterInFlight = true;
    return inFlight;
  }

  inFlight = saveIfComplete(store).finally(() => {
    inFlight = undefined;
  });
  await inFlight;

  if (rerunAfterInFlight) {
    rerunAfterInFlight = false;
    await syncInsuranceCoverageCheck(store);
  }
}

async function saveIfComplete(store: typeof baseStore): Promise<void> {
  const state = store.getState();
  if (!Auth.isUserSignedIn() || state.user.userId === undefined) return;
  const payload = buildInsuranceCoverageCheck(state, await getFlowName(state));
  if (payload === undefined) return;

  const content = contentOf(payload);
  if (content === lastSyncedContent) return;

  try {
    const { id } = await new ApiClient().saveInsuranceCoverageCheck(payload);
    lastSyncedContent = content;
    // Storing the id re-runs this effect via MERGE_USER_DATA; the unchanged content stops it there.
    if (payload.id !== id) dispatchUserData({ insuranceCoverageCheckId: [id] });
  } catch (error) {
    recordError(error);
  }
}
